import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../api/axiosConfig";
import { toast } from "react-hot-toast";
import { useAuthStore } from "../store/authStore";
import { adminApi } from "../api/adminApi";
import { ownerApi } from "../api/ownerApi";
import { 
    BarChart as BarIcon, 
    Calendar as CalendarIcon, 
    TrendingUp as TrendingIcon,
    LayoutGrid as GridIcon,
    ClipboardList as LogIcon
} from "lucide-react";

// Chart.js
import { 
    Chart as ChartJS, 
    CategoryScale, 
    LinearScale, 
    BarElement, 
    Title, 
    Tooltip, 
    Legend 
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const getManilaDate = (date = new Date()) => {
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
            timeZone: 'Asia/Manila',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(date).map(({ type, value }) => [type, value])
    );

    return `${parts.year}-${parts.month}-${parts.day}`;
};

const EnterpriseSalesReportPage = () => {
    const { mikrotikId: urlId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuthStore();

    const [devices, setDevices] = useState([]);
    const [vendos, setVendos] = useState([]); // List for filtering
    const [selectedId, setSelectedId] = useState(urlId || "");
    const [selectedVendoId, setSelectedVendoId] = useState(""); // Vendo filter state
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);

    const [dates, setDates] = useState(() => {
        const today = getManilaDate();
        return {
            start: `${today.slice(0, 7)}-01`,
            end: today,
        };
    });

    const api = user?.role === "admin" ? adminApi : ownerApi;

    // Fetch Devices
    useEffect(() => {
        const fetchDevices = async () => {
            if (!user) return;
            try {
                const deviceList = user.role === "admin" ? await api.getAllMikrotiks() : await api.getMikrotiks();
                const list = Array.isArray(deviceList) ? deviceList : [];
                setDevices(list);
                if (!urlId && !selectedId && list.length > 0) {
                    setSelectedId(list[0].id.toString());
                }
            } catch (err) {
                toast.error("Failed to load MikroTik devices");
            }
        };
        fetchDevices();
    }, [user]);

    useEffect(() => {
    const fetchVendos = async () => {
        if (!selectedId) return;
        try {
            // This calls GET /api/admin/mikrotiks/:id/vendos or /api/owner/...
            const response = await api.getVendoMachines(selectedId); 
            
            // Handle cases where the response might be { vendos: [...] } or just [...]
            const list = Array.isArray(response) ? response : (response.vendos || []);
            setVendos(list);
        } catch (err) {
            console.error("Failed to load vendos for dropdown");
            setVendos([]);
        }
    };
    fetchVendos();
}, [selectedId]);

    const fetchReport = async () => {
        if (!selectedId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`/sales-report/enterprise`, {
                params: { 
                    mikrotikId: selectedId, 
                    startDate: dates.start, 
                    endDate: dates.end,
                    vendoId: selectedVendoId // New filter param
                },
            });
            setReport(data);
        } catch (err) {
            toast.error("Error generating sales report");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedId) fetchReport();
    }, [selectedId, selectedVendoId]);

    const handleDeviceChange = (id) => {
        setSelectedId(id);
        setSelectedVendoId(""); // Reset filter on device change
        const base = user?.role === "admin" ? "/admin" : "/owner";
        navigate(`${base}/enterprise-sales/${id}`);
    };

    const chartData = useMemo(() => {
        const rows = Array.isArray(report?.chartData) ? report.chartData : [];
        return {
            labels: rows.map((r) => r.date),
            datasets: [
                {
                    label: "Combined Daily Revenue",
                    data: rows.map((r) => r.amount),
                    backgroundColor: "rgba(59, 130, 246, 0.8)",
                    hoverBackgroundColor: "rgba(59, 130, 246, 1)",
                    borderRadius: 8,
                    barThickness: 30,
                },
            ],
        };
    }, [report]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: "#1f2937",
                padding: 12,
                titleFont: { size: 14, weight: "bold" },
                bodyFont: { size: 13 },
                callbacks: {
                    label: (context) => ` ₱${context.raw.toLocaleString()}`,
                },
            },
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: "#9ca3af", font: { size: 11 } } },
            y: { 
                beginAtZero: true, 
                grid: { color: "rgba(229, 231, 235, 0.5)" },
                ticks: { color: "#9ca3af", font: { size: 11 }, callback: (v) => `₱${v}` }
            },
        },
    };

    return (
        <div className="min-h-full text-slate-900">
            <div className="mx-auto max-w-[1500px] space-y-4">
                {/* Header & Device Selection */}
                <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
                    <div>
                        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50"><TrendingIcon className="h-4 w-4 text-blue-600" /></span>
                            Financial Intelligence
                        </h1>
                        <p className="mt-1 text-xs text-slate-500">Revenue performance across devices and vendos</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                         <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
                            <span className="text-[10px] font-black uppercase text-gray-400">Device</span>
                            <select
                                className="bg-transparent border-none text-blue-600 font-bold focus:ring-0 cursor-pointer text-sm"
                                value={selectedId}
                                onChange={(e) => handleDeviceChange(e.target.value)}
                            >
                                <option value="">Select MikroTik</option>
                                {devices.map((d) => (
                                    <option key={d.id} value={d.id}>{d.deviceName}</option>
                                ))}
                            </select>
                        </div>
                         <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-sm">
                            <span className="text-[10px] font-black uppercase text-gray-400">Filter Vendo</span>
                            <select
                                className="bg-transparent border-none text-emerald-600 font-bold focus:ring-0 cursor-pointer text-sm"
                                value={selectedVendoId}
                                onChange={(e) => setSelectedVendoId(e.target.value)}
                            >
                                <option value="">All Vendos</option>
                                {vendos.map((v) => (
                                    <option key={v.id} value={v.id}>{v.vendoName}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Filters Section */}
                <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-500 flex items-center gap-2">
                            <CalendarIcon className="w-3 h-3" /> START DATE
                        </label>
                        <input
                            type="date"
                            className="block w-full rounded-xl border-gray-200 bg-gray-50 text-sm focus:ring-blue-500"
                            value={dates.start}
                            onChange={(e) => setDates({ ...dates, start: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-500 flex items-center gap-2">
                            <CalendarIcon className="w-3 h-3" /> END DATE
                        </label>
                        <input
                            type="date"
                            className="block w-full rounded-xl border-gray-200 bg-gray-50 text-sm focus:ring-blue-500"
                            value={dates.end}
                            onChange={(e) => setDates({ ...dates, end: e.target.value })}
                        />
                    </div>
                    <button
                        onClick={fetchReport}
                        disabled={loading || !selectedId}
                        className="btn-primary flex-1 md:flex-none"
                    >
                        {loading ? "Calculating..." : "Apply Analysis"}
                    </button>
                </div>

                {report ? (
                    <div className="animate-in fade-in duration-300">
                        {/* KPI Grid */}
                        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                            {[
                                { label: "Period Gross", val: `₱${(report?.summary?.totalSales || 0).toLocaleString()}`, color: "bg-emerald-500" },
                                { label: "Daily Average", val: `₱${report?.summary?.averagePerDay || "0.00"}`, color: "bg-blue-500" },
                                { label: "Data Integrity", val: `${report?.summary?.recordCount || 0} Data Days`, color: "bg-indigo-600" }
                            ].map((kpi, i) => (
                                <div key={i} className="flex flex-col justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                    <span className="text-[10px] font-black uppercase text-gray-400 tracking-widest">{kpi.label}</span>
                                    <div className="flex items-end justify-between mt-2">
                                        <h2 className="text-2xl font-bold tracking-tight text-slate-900">{kpi.val}</h2>
                                        <div className={`h-6 w-1 rounded-full ${kpi.color}`} />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Income Per Vendo Breakdown */}
                        <div className="mb-4">
                            <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <GridIcon className="w-3 h-3" /> Income Per Vendo
                            </h3>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                                {report.vendoBreakdown.map((v, i) => (
                                    <div key={i} className="group rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-colors hover:border-blue-200">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase">{v.name}</p>
                                        <h4 className="mt-1 text-lg font-bold text-blue-600">₱{v.total.toLocaleString()}</h4>
                                        <div className="w-full bg-gray-100 h-1.5 rounded-full mt-3 overflow-hidden">
                                            <div 
                                                className="bg-blue-500 h-full transition-all duration-1000" 
                                                style={{ width: `${(v.total / report.summary.totalSales) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Chart View */}
                        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="font-black text-lg text-gray-800 flex items-center gap-2">
                                    <BarIcon className="w-5 h-5 text-blue-600" /> Revenue Trend-line (Aggregate)
                                </h3>
                            </div>
                            <div className="h-[300px]">
                                <Bar data={chartData} options={chartOptions} />
                            </div>
                        </div>

                        {/* Granular Sales Log - Split per Vendo */}
                        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-3">
                                <h3 className="font-bold text-sm text-gray-700 uppercase tracking-tight flex items-center gap-2">
                                    <LogIcon className="w-4 h-4" /> Granular Sales Log
                                </h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="text-[10px] font-black uppercase text-gray-400 border-b border-gray-100">
                                            <th className="px-4 py-2.5">Sync Date</th>
                                            <th className="px-4 py-2.5">Source Vendo</th>
                                            <th className="px-4 py-2.5 text-right">Revenue (PHP)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {report.rawLogs.map((row, idx) => (
                                            <tr key={idx} className="group hover:bg-blue-50/30 transition-colors">
                                                <td className="px-4 py-2.5 text-xs font-semibold text-slate-600">{row.reportDate}</td>
                                                <td className="px-4 py-2.5">
                                                    <span className="px-3 py-1 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-lg border border-blue-100">
                                                        {row.vendo.vendoName}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2.5 text-right text-sm font-bold text-blue-600">
                                                    ₱{Number(row.dailyTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="py-32 flex flex-col items-center justify-center bg-white rounded-[2.5rem] border-2 border-dashed border-gray-200">
                        <BarIcon className="w-10 h-10 text-gray-300 mb-4" />
                        <p className="text-gray-400 font-bold">Select a MikroTik device to generate visualized analysis.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EnterpriseSalesReportPage;
